import { z } from "zod";
import { INSIGHTS_METRIC_OPTIONS } from "./constants.ts";
import { ARRAY_FILTERS, FilterTypesEnum } from "./filter-types.ts";
import { channelSchema } from "./schemas.ts";

const insightMetricValues = INSIGHTS_METRIC_OPTIONS.map(
	(option) => option.value,
) as [string, ...string[]];
const insightMetricSchema = z.enum(insightMetricValues);

const customerTypeValues = ["total", "new", "returning"] as const;
const customerTypeSchema = z.enum(customerTypeValues);

export const breakdownDimensionValues = [
	"channel",
	"campaign",
	"adset",
	"ad",
	"creative",
] as const;
const breakdownDimensionSchema = z.enum(breakdownDimensionValues);
const breakdownSortOrderSchema = z.enum(["asc", "desc"]);

export const insightTypeValues = [
	"line",
	"area",
	"bar",
	"stacked_bar",
	"line_cumulative",
	"number",
	"pie",
	"bar_horizontal",
	"table",
] as const;

export const timeRangePresetValues = [
	"today",
	"yesterday",
	"last_7_days",
	"last_14_days",
	"last_30_days",
	"last_90_days",
	"this_week",
	"last_week",
	"this_month",
	"last_month",
	"year_to_date",
	"last_custom_days",
] as const;

export const comparisonPeriodValues = [
	"no_comparison",
	"last_period",
	"last_week",
	"last_month",
	"last_quarter",
	"last_year",
] as const;

export const granularityValues = [
	"daily",
	"weekly",
	"monthly",
	"none",
] as const;
const insightGranularitySchema = z.enum(granularityValues);
const filterOperatorSchema = z.enum(["and", "or"]);

export const INSIGHT_BREAKDOWN_DEFAULT_LIMIT = 25;
export const INSIGHT_BREAKDOWN_MAX_LIMIT = 25;

const genericFilterFieldValues = [
	"campaign_id",
	"campaign_name",
	"adset_id",
	"adset_name",
	"ad_id",
	"ad_name",
] as const;

const filterableNameValues = [
	"channel",
	"customer_type",
	...genericFilterFieldValues,
] as const;
const filterableNameSchema = z.enum(filterableNameValues);

export type FilterableName = z.infer<typeof filterableNameSchema>;

export const FILTER_FIELD_OPTIONS: {
	label: string;
	value: FilterableName;
	operators: FilterTypesEnum[];
}[] = [
	{ label: "Channel", value: "channel", operators: [FilterTypesEnum.in] },
	{
		label: "Customer type",
		value: "customer_type",
		operators: [FilterTypesEnum.eq],
	},
	{
		label: "Campaign ID",
		value: "campaign_id",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
	{
		label: "Campaign Name",
		value: "campaign_name",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
	{
		label: "Ad Set ID",
		value: "adset_id",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
	{
		label: "Ad Set Name",
		value: "adset_name",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
	{
		label: "Ad ID",
		value: "ad_id",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
	{
		label: "Ad Name",
		value: "ad_name",
		operators: [
			FilterTypesEnum.eq,
			FilterTypesEnum.ne,
			FilterTypesEnum.icontains,
			FilterTypesEnum.not_icontains,
		],
	},
];

const filterIdSchema = z.string().min(1);
const scalarOrArrayValueSchema = z.union([
	z.string(),
	z.number(),
	z.array(z.union([z.string(), z.number()])),
]);

const filterOperatorsByField = new Map(
	FILTER_FIELD_OPTIONS.map((option) => [
		option.value,
		new Set(option.operators),
	]),
);

const createGenericFilterSchema = (
	name: (typeof genericFilterFieldValues)[number],
) =>
	z
		.object({
			id: filterIdSchema,
			name: z.literal(name),
			operator: z.nativeEnum(FilterTypesEnum),
			value: scalarOrArrayValueSchema,
		})
		.superRefine((filter, ctx) => {
			const allowedOperators = filterOperatorsByField.get(filter.name);
			if (!allowedOperators?.has(filter.operator)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["operator"],
					message: `Operator '${filter.operator}' is not allowed for ${filter.name}.`,
				});
			}

			const expectsArray = ARRAY_FILTERS.includes(filter.operator);
			if (expectsArray && !Array.isArray(filter.value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["value"],
					message: `Operator '${filter.operator}' requires an array value.`,
				});
			}

			if (!expectsArray && Array.isArray(filter.value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["value"],
					message: `Operator '${filter.operator}' requires a scalar value.`,
				});
			}
		});

const genericFilterSchemas = [
	createGenericFilterSchema("campaign_id"),
	createGenericFilterSchema("campaign_name"),
	createGenericFilterSchema("adset_id"),
	createGenericFilterSchema("adset_name"),
	createGenericFilterSchema("ad_id"),
	createGenericFilterSchema("ad_name"),
] as const;

const channelFilterSchema = z.object({
	id: filterIdSchema,
	name: z.literal("channel"),
	operator: z.literal(FilterTypesEnum.in),
	value: z.array(channelSchema).min(1),
});

const customerTypeFilterSchema = z
	.object({
		id: filterIdSchema,
		name: z.literal("customer_type"),
		operator: z.literal(FilterTypesEnum.eq),
		value: customerTypeSchema,
	})
	.describe(
		"Customer type insight filter. Use name='customer_type', operator='eq', and value 'total', 'new', or 'returning'. Do not use 'newVsReturning' as a filter name.",
	);

export const insightFilterSchema = z.discriminatedUnion("name", [
	channelFilterSchema,
	customerTypeFilterSchema,
	...genericFilterSchemas,
]);

const insightSeriesBaseSchema = z.object({
	metric: insightMetricSchema,
	filters: z.array(insightFilterSchema).optional(),
});

const insightSeriesCreateSchema = insightSeriesBaseSchema.extend({
	id: z.string().min(1).optional(),
	label: z.string().min(1).optional(),
	color: z.string().min(1).optional(),
});

const insightSeriesUpdateSchema = insightSeriesBaseSchema.extend({
	id: z.string().min(1),
	label: z.string().min(1),
	color: z.string().min(1),
});

const insightBreakdownPatchSchema = z.object({
	dimension: breakdownDimensionSchema,
	limit: z.number().int().min(1).max(INSIGHT_BREAKDOWN_MAX_LIMIT).optional(),
	sortBy: insightMetricSchema.optional(),
	sortOrder: breakdownSortOrderSchema.optional(),
});

const insightBreakdownStrictSchema = z.object({
	dimension: breakdownDimensionSchema,
	limit: z.number().int().min(1).max(INSIGHT_BREAKDOWN_MAX_LIMIT),
	sortBy: insightMetricSchema,
	sortOrder: breakdownSortOrderSchema,
});

export const insightTimeRangeSchema = z.object({
	preset: z.enum(timeRangePresetValues),
	customDays: z.number().int().min(1).max(3650).optional(),
});

const yAxisUnitSchema = z.object({
	type: z.enum([
		"none",
		"duration_s",
		"duration_ms",
		"percent_1",
		"currency",
		"custom_prefix",
		"custom_postfix",
	]),
	customValue: z.string().optional(),
});

export const insightDisplayOptionsSchema = z.object({
	showLegend: z.boolean().optional(),
	showMultipleYAxes: z.boolean().optional(),
	showSummaryStats: z.boolean().optional(),
	yAxisUnit: yAxisUnitSchema.optional(),
});

const insightDisplayOptionsStrictSchema = z.object({
	showLegend: z.boolean(),
	showMultipleYAxes: z.boolean(),
	showSummaryStats: z.boolean(),
	yAxisUnit: yAxisUnitSchema.optional(),
});

const dashboardLayoutSchema = z
	.object({
		x: z.number().int().min(0),
		y: z.number().int().min(0),
		w: z.number().int().min(0),
		h: z.number().int().min(0),
		min_w: z.number().int().min(0).optional(),
		max_w: z.number().int().min(0).optional(),
		min_h: z.number().int().min(0).optional(),
		max_h: z.number().int().min(0).optional(),
	})
	.strict()
	.describe(
		"Tile layout for one breakpoint. Must include x, y, w, and h. For dashboard tool writes, include layouts for both lg and sm whenever you generate a new arrangement.",
	);

const dashboardTileSchema = z
	.object({
		insight_id: z.string().trim().min(1),
		layouts: z.record(z.string(), dashboardLayoutSchema),
		tile_order: z.number().int().min(0).optional(),
	})
	.strict()
	.describe(
		"Dashboard tile. Every tile must include insight_id and layouts. Never send a reorder-only tile with just insight_id or tile_order.",
	);

const dashboardDefaultTimeRangeSchema = z
	.object({
		preset: z.enum(timeRangePresetValues),
		custom_days: z.number().int().min(0).optional(),
	})
	.strict();

const sharedInsightPatchFields = {
	title: z.string().min(1).max(120).optional(),
	description: z.string().max(400).optional(),
	filters: z
		.array(insightFilterSchema)
		.optional()
		.describe(
			"Insight-level filters. Use exact filter names from the schema. For customer type, use { name: 'customer_type', operator: 'eq', value: 'total' | 'new' | 'returning' }; do not use 'newVsReturning' inside filters.",
		),
	filterOperator: filterOperatorSchema.optional(),
	breakdown: z
		.array(insightBreakdownPatchSchema)
		.max(1, "Only one breakdown dimension is currently supported.")
		.optional(),
	timeRange: insightTimeRangeSchema.optional(),
	useOwnTimeRange: z.boolean().optional(),
	granularity: insightGranularitySchema.optional(),
	comparisonPeriod: z.enum(comparisonPeriodValues).optional(),
	attributionPreset: z.string().optional(),
	type: z.enum(insightTypeValues).optional(),
	displayOptions: insightDisplayOptionsSchema.optional(),
} as const;

export const createInsightInputSchema = z
	.object({
		...sharedInsightPatchFields,
		series: z.array(insightSeriesCreateSchema).min(1).optional(),
	})
	.strict();

export const updateInsightPatchSchema = z
	.object({
		...sharedInsightPatchFields,
		series: z.array(insightSeriesUpdateSchema).min(1).optional(),
	})
	.strict();

export const insightConfigStrictSchema = z
	.object({
		id: z.string().min(1),
		title: z.string().min(1).max(120),
		description: z.string().max(400).optional(),
		series: z.array(insightSeriesUpdateSchema).min(1),
		filters: z.array(insightFilterSchema),
		filterOperator: filterOperatorSchema,
		breakdown: z
			.array(insightBreakdownStrictSchema)
			.max(1, "Only one breakdown dimension is currently supported."),
		timeRange: insightTimeRangeSchema,
		useOwnTimeRange: z.boolean().optional(),
		granularity: insightGranularitySchema,
		comparisonPeriod: z.enum(comparisonPeriodValues),
		attributionPreset: z.string(),
		type: z.enum(insightTypeValues),
		displayOptions: insightDisplayOptionsStrictSchema.optional(),
		schemaVersion: z.number().int().min(1),
		createdAt: z.number().int().min(0),
		updatedAt: z.number().int().min(0),
	})
	.strict();

const dashboardBaseSchema = {
	title: z.string().trim().min(1).max(120).optional(),
	description: z.string().max(500).optional(),
	tiles: z
		.array(dashboardTileSchema)
		.optional()
		.describe(
			"Full dashboard tiles array for this write. If provided, every tile must include layouts. For updateDashboard, tiles is a full replacement, not a partial patch.",
		),
	default_time_range: dashboardDefaultTimeRangeSchema.nullable().optional(),
	default_attribution_preset_id: z.string().nullable().optional(),
} as const;

export const dashboardCreateInputSchema = z
	.object({
		title: z.string().trim().min(1).max(120).default("Untitled Dashboard"),
		description: z.string().max(500).default(""),
		tiles: z
			.array(dashboardTileSchema)
			.default([])
			.describe(
				"Initial dashboard tiles. Every tile must include layouts. When generating a new dashboard, usually include both lg and sm layouts on every tile.",
			),
		default_time_range: dashboardDefaultTimeRangeSchema.default({
			preset: "today",
			custom_days: 0,
		}),
		default_attribution_preset_id: z.string().nullable().default(null),
	})
	.strict();

export const dashboardPatchSchema = z.object(dashboardBaseSchema).strict();

export const listInputSchema = z
	.object({
		query: z.string().trim().optional(),
		limit: z.number().int().min(1).max(50).optional(),
		offset: z.number().int().min(0).optional(),
	})
	.strict();

export type CreateInsightInput = z.infer<typeof createInsightInputSchema>;
export type UpdateInsightPatchInput = z.infer<typeof updateInsightPatchSchema>;
export type DashboardCreateInput = z.infer<typeof dashboardCreateInputSchema>;
export type DashboardPatchInput = z.infer<typeof dashboardPatchSchema>;
export type InsightSeriesInput = z.infer<typeof insightSeriesCreateSchema>;
export type InsightPatchInput = CreateInsightInput;
export type CustomerType = z.infer<typeof customerTypeSchema>;
export type FilterOperator = z.infer<typeof filterOperatorSchema>;
export type BreakdownDimension = z.infer<typeof breakdownDimensionSchema>;
export type BreakdownSortOrder = z.infer<typeof breakdownSortOrderSchema>;
export type Granularity = z.infer<typeof insightGranularitySchema>;
export type BreakdownConfig = z.infer<typeof insightBreakdownStrictSchema>;
export type InsightFilterConfig = z.infer<typeof insightFilterSchema>;
export type InsightSeries = z.infer<typeof insightSeriesUpdateSchema>;
export type InsightConfig = z.infer<typeof insightConfigStrictSchema>;

export const defaultBreakdownLimit = INSIGHT_BREAKDOWN_DEFAULT_LIMIT;
